import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, ArrowRight, Tag } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { API } from '../App';

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Demo articles for display
  const demoArticles = [
    {
      article_id: 'demo-1',
      title: 'Comment créer une entreprise au Sénégal : Guide complet',
      slug: 'comment-creer-entreprise-senegal',
      excerpt: 'Découvrez toutes les étapes pour formaliser votre entreprise au Sénégal, du choix de la forme juridique à l\'immatriculation.',
      category: 'Création d\'entreprise',
      image_url: 'https://images.unsplash.com/photo-1664575602554-2087b04935a5?w=800',
      author_name: 'Cheikh Abdoul Aziz COULIBALY',
      created_at: '2024-01-15',
      published: true
    },
    {
      article_id: 'demo-2',
      title: 'Les 5 erreurs juridiques à éviter en tant qu\'entrepreneur',
      slug: 'erreurs-juridiques-entrepreneur',
      excerpt: 'Protégez votre entreprise en évitant ces erreurs courantes qui peuvent coûter cher aux entrepreneurs débutants.',
      category: 'Conseil juridique',
      image_url: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800',
      author_name: 'Cheikh Abdoul Aziz COULIBALY',
      created_at: '2024-01-10',
      published: true
    },
    {
      article_id: 'demo-3',
      title: 'Financement des startups : Options disponibles au Sénégal',
      slug: 'financement-startups-senegal',
      excerpt: 'Tour d\'horizon des différentes sources de financement accessibles aux startups et PME sénégalaises.',
      category: 'Financement',
      image_url: 'https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?w=800',
      author_name: 'Cheikh Talibouya Camara NDIAYE',
      created_at: '2024-01-05',
      published: true
    },
    {
      article_id: 'demo-4',
      title: 'La méthode des 3F expliquée en détail',
      slug: 'methode-3f-expliquee',
      excerpt: 'Découvrez comment la méthode Formation, Formalisation, Financement peut transformer votre projet entrepreneurial.',
      category: 'Formation',
      image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800',
      author_name: 'Cheikh Talibouya Camara NDIAYE',
      created_at: '2024-01-01',
      published: true
    }
  ];

  const categories = ['all', 'Création d\'entreprise', 'Conseil juridique', 'Financement', 'Formation'];

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API}/articles?published_only=true`);
        if (response.ok) {
          const data = await response.json();
          // Combine API articles with demo articles if no real articles exist
          if (data.length > 0) {
            setArticles(data);
          } else {
            setArticles(demoArticles);
          }
        } else {
          setArticles(demoArticles);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles(demoArticles);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Blog & Ressources</span>
            <h1 className="hero-title mt-4" data-testid="blog-title">
              Conseils et <span className="text-[#006F33]">actualités</span>
            </h1>
            <p className="hero-subtitle mt-6">
              Articles, guides et ressources pour vous aider dans votre parcours entrepreneurial au Sénégal.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="blog-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Search */}
            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-sunulex"
                data-testid="blog-search"
              />
            </div>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#006F33] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`category-${category}`}
                >
                  {category === 'all' ? 'Tous' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 bg-[#F9F9F7]" data-testid="blog-articles">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner w-12 h-12"></div>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500 text-lg">Aucun article trouvé.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <article key={article.article_id} className="blog-card" data-testid={`article-${article.article_id}`}>
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.image_url || 'https://images.unsplash.com/photo-1664575602554-2087b04935a5?w=800'}
                      alt={article.title}
                      className="blog-image w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-4 h-4 text-[#006F33]" />
                      <span className="text-sm text-[#006F33] font-medium">{article.category}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(article.created_at)}</span>
                      </div>
                      <Link 
                        to={`/blog/${article.slug}`}
                        className="flex items-center gap-1 text-[#006F33] font-medium hover:gap-2 transition-all"
                        data-testid={`read-more-${article.article_id}`}
                      >
                        Lire <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-white" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-[#006F33] rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Restez informé
            </h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto">
              Abonnez-vous à notre newsletter pour recevoir nos derniers articles et conseils directement dans votre boîte mail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Votre email"
                className="flex-grow bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-white"
                data-testid="newsletter-email"
              />
              <button className="bg-white text-[#006F33] hover:bg-slate-100 px-6 py-2 rounded-lg font-medium transition-colors" data-testid="newsletter-submit">
                S'abonner
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
